// getProjectInformation.ts

import { useFunctionsStore } from '@/store/functions.store';
// NOTE: Do not inject defaults for project fields here; return source-of-truth only
import { DynamicProject } from "@/types/store"
// Use auth from @/firebase barrel which exports the resilient Auth proxy
// that handles async initialization gracefully (returns null for currentUser if not ready)
import { auth } from '@/firebase';

// Request deduplication cache
const requestCache = new Map<string, Promise<DynamicProject<any> | null>>();
const resultCache = new Map<string, { data: DynamicProject<any> | null, timestamp: number }>();
const CACHE_TTL = 30000; // 30 seconds cache TTL for project info
const REQUEST_TTL = 10000; // 10 seconds for in-flight request deduplication

// Register cache with global cleanup manager
import { registerPerformanceCache } from '@/utils/cache-manager';
registerPerformanceCache('project-info-result', resultCache, CACHE_TTL, 200);

/**
 * Fetches project information based on the provided project ID and parameters.
 * 
 * @async
 * @function getProjectInformation
 * @param {string} projectId - The ID of the project for which to fetch information.
 * @param {string[]} [params=['info']] - An array of strings specifying which parameters to fetch for the project. Defaults to ['info'].
 * @param {boolean} [forRegistration=false] - Whether this is for a registration route (allows unauthenticated access)
 * @returns {Promise<DynamicProjectInformationResponse<typeof params>|null>} A Promise that resolves to an object containing the project information, or null if the fetch fails.
 * @throws Will throw an error if the underlying API call fails.
 */
export const getProjectInformation = async <T extends string[] = ['info']>(
    projectId: string,
    params: T = ['info'] as T,
    forRegistration: boolean = false
): Promise<DynamicProject<T> | null> => {
    // Validate projectId
    if (!projectId || projectId.trim() === '') {
        console.error('[getProjectInformation] ❌ Invalid projectId:', { projectId });
        return null;
    }
    
    // Create cache key including auth status for security
    const authUid = auth.currentUser?.uid || 'anonymous';
    const cacheKey = `${projectId}:${JSON.stringify(params)}:${forRegistration}:${authUid}`;
    const now = Date.now();
    
    // Check result cache first
    const cached = resultCache.get(cacheKey);
    if (cached && (now - cached.timestamp) < CACHE_TTL) {
        console.log('[getProjectInformation] 📦 Using cached result:', { projectId, params, cacheAge: now - cached.timestamp });
        return cached.data as DynamicProject<T> | null;
    }
    
    // Check if there's already an in-flight request for the same parameters
    const existingRequest = requestCache.get(cacheKey);
    if (existingRequest) {
        console.log('[getProjectInformation] ⏳ Reusing in-flight request:', { projectId, params });
        try {
            const result = await existingRequest;
            return result as DynamicProject<T> | null;
        } catch (error) {
            // If the shared request failed, remove it and continue with a new request
            requestCache.delete(cacheKey);
        }
    }
    
    const functionsStore = useFunctionsStore();
    
    console.log('[getProjectInformation] 🎯 Starting project information fetch:', {
        projectId,
        params,
        forRegistration,
        hasAuth: !!auth.currentUser,
        authUid: auth.currentUser?.uid
    });
    
    // Create the request promise and cache it to prevent duplicate calls
    const requestPromise = (async (): Promise<DynamicProject<T> | null> => {
        try {
            // Prepare the function payload
            const functionPayload: any = {
                action: 'get',
                path: `projects/${projectId}`,
                payload: { params }
            };
            
            // Add secret key for demo projects OR for registration routes when user is not authenticated
            const currentUser = auth.currentUser;
            const needsSecretKey = projectId === 'demo' || (forRegistration && !currentUser);
            // Development helper: allow secret for local testing when no user is logged in
            const devNoUser = import.meta.env.DEV && !currentUser;
            
            if (needsSecretKey || devNoUser) {
                functionPayload.secretKey = import.meta.env.VITE_FIREBASE_SHIRLY_SECRET_KEY;
                console.log('[getProjectInformation] 🔑 Using secret key for:', { 
                    projectId, 
                    forRegistration, 
                    hasUser: !!currentUser,
                    reason: projectId === 'demo' ? 'demo project' : (forRegistration ? 'registration without auth' : 'development without auth')
                });
            }
            
            console.log('[getProjectInformation] 📤 Calling Firebase function:', {
                functionName: 'firekitTsOnCallFirestoreSwitch',
                path: functionPayload.path,
                params: functionPayload.payload.params,
                hasSecretKey: !!functionPayload.secretKey
            });
            
            const response = await functionsStore.callFunction<any>({
                function_name: 'firekitTsOnCallFirestoreSwitch',
                function_payload: functionPayload
            });
            
            if (response) {
                // Log groups field specifically for debugging
                console.log('[getProjectInformation] 📊 Response analysis:', {
                    requestedParams: params,
                    responseKeys: Object.keys(response),
                    hasGroupsInResponse: 'groups' in response,
                    groupsValue: (response as any).groups
                });
                
                // Optional deep debug of raw response payload (dev or explicit opt-in)
                try {
                    if (import.meta.env.DEV || (import.meta.env as any).VITE_LOG_PROJECT_INFO === 'true') {
                        const rawJson = JSON.stringify(response);
                        const infoJson = JSON.stringify((response as any).info ?? null);
                        console.log('[getProjectInformation] 🧾 RAW RESPONSE:', rawJson);
                        console.log('[getProjectInformation] 🧾 RAW INFO BLOCK:', infoJson);
                    }
                } catch (e) {
                    console.warn('[getProjectInformation] Unable to stringify raw response for logging');
                }

                console.log('[getProjectInformation] ✅ Successfully fetched project information:', {
                    projectId,
                    fields: Object.keys(response),
                    hasInfo: !!response.info,
                    infoStructure: response.info ? {
                        project_id: response.info.project_id,
                        project_title: response.info.project_title,
                        hasProjectImage: !!response.info.project_image,
                        projectImageStructure: response.info.project_image ? {
                            public_id: response.info.project_image.public_id,
                            url: response.info.project_image.url,
                            hasPublicId: !!response.info.project_image.public_id,
                            hasUrl: !!response.info.project_image.url,
                            keys: Object.keys(response.info.project_image)
                        } : null
                    } : null
                });
                
                // Cache the successful result
                resultCache.set(cacheKey, { data: response as DynamicProject<T>, timestamp: now });
                
                return response as DynamicProject<T>; // We cast here, assuming the data aligns with our dynamic type
            } else {
                console.warn('[getProjectInformation] ⚠️ Empty response from Firebase function');
                
                // Cache null result for a shorter time to allow retries
                resultCache.set(cacheKey, { data: null, timestamp: now });
                
                return null; // Return null if there's no information
            }
        } catch (error: any) {
            console.error('[getProjectInformation] ❌ Error fetching project information:', {
                projectId,
                params,
                error: error.message || error,
                code: error.code,
                details: error.details,
                stack: error.stack,
                forRegistration,
                hasAuth: !!auth.currentUser
            });
            
            // Re-throw with enhanced error info if it's a function error
            if (error.code === 'functions/internal') {
                console.error('[getProjectInformation] 🔴 Firebase function internal error - this may indicate:', [
                    '1. The project document does not exist',
                    '2. Permission issues accessing the project',
                    '3. Invalid field parameters',
                    '4. Server-side error in the Firebase function'
                ].join('\n'));
            }
            
            // Don't cache errors - allow immediate retries
            return null; // Handle the error as needed, possibly by returning null
        } finally {
            // Remove the request from cache after completion
            requestCache.delete(cacheKey);
        }
    })();
    
    // Cache the request promise to prevent duplicate calls
    requestCache.set(cacheKey, requestPromise);
    
    // Automatically clean up the request cache after TTL
    setTimeout(() => {
        requestCache.delete(cacheKey);
    }, REQUEST_TTL);
    
    return requestPromise;
};
